import { trackMenuNavigation } from '../menuAnalytics';
import * as translator from '../../../../utils/translator';
import { FoundayoMenuPromoMain } from './FoundayoMenuPromos';

interface TopCategoriesSectionProps {
  categories: SidebarTopCategory[];
  onCategoryClick: (category: SidebarTopCategory) => void;
  language: string;
  country: string;
}

export function TopCategoriesSection({
  categories,
  onCategoryClick,
  language,
  country
}: TopCategoriesSectionProps) {
  const DE_OVERWEIGHT_MENU_ICON = 'overweight_menu_icon';

  const getRedesignAssetBasePath = () => {
    const rawBase = (window as any).clientConfig?.assetBasePath || 'https://images.doktorabc.com/redesign_2025';
    return rawBase.includes('/redesign_2025')
      ? rawBase.replace(/\/$/, '')
      : `${rawBase.replace(/\/$/, '')}/redesign_2025`;
  };

  const isDeWeightLoss = (mention: string, assetMention?: string) =>
    country?.toUpperCase() === 'DE' && (mention === 'weight_loss' || assetMention === 'weight_loss');

  const getMenuIconBase = (mention: string, assetMention?: string) => {
    const assetBasePath = getRedesignAssetBasePath();
    if (isDeWeightLoss(mention, assetMention)) {
      return `${assetBasePath}/menu/weight_loss/menu_icon/${DE_OVERWEIGHT_MENU_ICON}/${DE_OVERWEIGHT_MENU_ICON}`;
    }
    const actualMention = assetMention || mention;
    return `${assetBasePath}/menu/${actualMention}/menu_icon/menu_icon`;
  };

  const getAssetUrl = (mention: string, assetMention?: string) => {
    return `${getMenuIconBase(mention, assetMention)}.png`;
  };

  const getAssetUrlWebp = (mention: string, assetMention?: string) => {
    return `${getMenuIconBase(mention, assetMention)}.webp`;
  };

  const handleCategoryClick = (category: SidebarTopCategory) => {
    onCategoryClick(category);
  };

  const handleCategoryLinkClick = (category: SidebarTopCategory, e: React.MouseEvent) => {
    e.preventDefault();
    trackMenuNavigation({
      category: category.mixpanel_title || category.label,
      button_label: category.label,
      carousel: 'no',
      sub_category: '',
      link: category.url,
    });
    if (category.url) {
      window.location.href = category.url;
    }
  };

  const categoriesWithIcons = categories.filter(cat => cat.mention);
  const isGb = (country || '').toUpperCase() === 'GB';
  const weightLossUrl = categories.find((c) => c.mention === 'weight_loss')?.url;

  return (
    <>
    {isGb && (
      <FoundayoMenuPromoMain language={language} weightLossUrl={weightLossUrl} />
    )}
    <div id="top-categories-section" className="px-3 md:px-4 pt-4">
      <span className="font-poppins font-medium block text-[#757575] tracking-[1px] uppercase leading-[26px] md:text-[15px] text-[13px] mb-3 px-2">
        {translator.translate(language, 'layout', 'sidebar/top_categories')}
      </span>
      <div className="flex flex-col gap-[9px]">
        {categoriesWithIcons.map((category) => {
          const isTestLab = category.mention === 'test_lab' && country?.toUpperCase() !== 'DE'; // DE: no gradient highlight
          
          // Direct link categories (isOtc or isDirectLink like test_lab)
          if (category.isOtc || category.isDirectLink) {
            return (
              <a
                key={category.mention}
                id={`rx-top-category-link-${category.mention}`}
                href={category.url}
                onClick={(e) => handleCategoryLinkClick(category, e)}
                className={`flex items-center overflow-hidden ${isTestLab ? 'relative ' : ''}w-full cursor-pointer bg-[#f2f4f6] h-[4rem] hover:bg-[#eaecef] rounded-[1.25rem]${isTestLab ? ' test-lab-button' : ''}`}
                style={{ transition: 'background-color 0.2s ease', textDecoration: 'none' }}
                data-category={category.mixpanel_title || category.label}
                data-button-label={category.label}
              >
                {isTestLab && <div className="test-lab-ellipse" aria-hidden="true"></div>}
                <div className={isTestLab ? "flex items-center w-full h-full relative z-10" : "flex items-center w-full h-full"}>
                  <div className={`w-[4.25rem] h-full flex items-end justify-start shrink-0 overflow-hidden${category.mention === 'weight_loss' ? ' pr-2' : ''}`}>
                    <picture className={`flex h-full items-end${category.mention === 'weight_loss' ? ' ml-[-0.75rem]' : ''}`}>
                      <source srcSet={getAssetUrlWebp(category.mention || '', category.assetMention)} type="image/webp" />
                      <img
                        src={getAssetUrl(category.mention || '', category.assetMention)}
                        alt=""
                        className="object-contain w-auto object-bottom h-full"
                        style={{ transition: 'opacity .4s ease-in-out' }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                        loading="lazy"
                        decoding="async"
                      />
                    </picture>
                  </div>
                  <div className="flex items-center flex-1 gap-3 min-w-0">
                    <span className="font-poppins font-medium text-[#1f2937] leading-[21px] text-[16px] tracking-normal">{category.label}</span>
                    {category.withNewTagFromMenu && (
                      <span className="text-white linear-background-color-primary font-semibold text-xs p-1 rounded-[5px] shrink-0">
                        {translator.translate(language, 'layout', 'sidebar/new_tag')}
                      </span>
                    )}
                  </div>
                  <div className="pr-4 shrink-0">
                    <img src="/img_v4/icons/chevron-right-icon.svg" alt="" aria-hidden="true" className="h-[9.33px] w-[5.33px]" style={{ color: '#98a0af' }} />
                  </div>
                </div>
              </a>
            );
          }

          return (
            <button
              key={category.mention}
              id={`rx-top-category-btn-${category.mention}`}
              className={`flex items-center overflow-hidden cursor-pointer bg-[#f2f4f6] h-[4rem] hover:bg-[#eaecef] rounded-[1.25rem] w-full border-none text-left${isTestLab ? ' relative test-lab-button' : ''}`}
              style={{ transition: 'background-color 0.2s ease' }}
              onClick={() => handleCategoryClick(category)}
              type="button"
              data-has-submenu="1"
              data-category={category.mixpanel_title || category.label}
            >
              {isTestLab && <div className="test-lab-ellipse" aria-hidden="true"></div>}
              <div className={isTestLab ? "flex items-center w-full h-full relative z-10" : "flex items-center w-full h-full"}>
                <div className={`w-[4.25rem] h-full flex items-end justify-start shrink-0 overflow-hidden${category.mention === 'weight_loss' ? ' pr-2' : ''}`}>
                  <picture className={`flex h-full items-end${category.mention === 'weight_loss' ? ' ml-[-0.75rem]' : ''}`}>
                    <source srcSet={getAssetUrlWebp(category.mention || '', category.assetMention)} type="image/webp" />
                    <img
                      src={getAssetUrl(category.mention || '', category.assetMention)}
                      alt=""
                      className="object-contain w-auto object-bottom h-full"
                      style={{ transition: 'opacity .4s ease-in-out' }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                      loading="lazy"
                      decoding="async"
                    />
                  </picture>
                </div>
                <div className="flex items-center flex-1 gap-3 min-w-0">
                  <span className="font-poppins font-medium text-[#1f2937] leading-[21px] text-[16px] tracking-normal">{category.label}</span>
                  {category.withNewTagFromMenu && (
                    <span className="text-white linear-background-color-primary font-semibold text-xs p-1 rounded-[5px] shrink-0">
                      {translator.translate(language, 'layout', 'sidebar/new_tag')}
                    </span>
                  )}
                </div>
                <div className="pr-4 shrink-0">
                  <img src="/img_v4/icons/chevron-right-icon.svg" alt="" aria-hidden="true" className="h-[9.33px] w-[5.33px]" style={{ color: '#98a0af' }} />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
    </>
  );
}
